import {
  ContentfulBackgroundImage,
  ContentfulCallToAction,
  ContentfulRichText,
} from '@pcln/contentful-components'
import { themeGet } from '@styled-system/theme-get'
import { Absolute, Badge, Box, Button, Flex, Image, Relative, Text } from 'pcln-design-system'
import React, { useRef } from 'react'
import styled, { css } from 'styled-components'
import CouponClipboard from '../CouponClipboard/CouponClipboard'

const CONDENSE_PROPS = {
  sm: {
    backgroundMediaHeight: '120px',
    badgePosition: 12,
    bodyTextStyle: 'caption',
    borderSpacing: '12px',
    elementSpacing: 1,
    foregroundMediaHeight: '80px',
    headingTextStyle: 'heading6',
    listMargin: 1,
  },
  md: {
    backgroundMediaHeight: '136px',
    badgePosition: 16,
    bodyTextStyle: 'paragraph',
    borderSpacing: 3,
    elementSpacing: 2,
    foregroundMediaHeight: '96px',
    headingTextStyle: 'heading5',
    listMargin: 2,
  },
  lg: {
    backgroundMediaHeight: '200px',
    badgePosition: 16,
    bodyTextStyle: 'paragraph',
    borderSpacing: 3,
    elementSpacing: 2,
    foregroundMediaHeight: '160px',
    headingTextStyle: 'heading4',
    listMargin: 2,
  },
}

const BackgroundImage = styled(ContentfulBackgroundImage)`
  background-position: ${(props) => props.objectPosition};
  background-size: ${(props) => props.objectFit};
`

const ContainerFlex = styled(Flex)`
  transition: box-shadow ${themeGet('timingFunctions.easeInOut')} ${themeGet('duration.normal')};

  &:hover {
    box-shadow: ${(props) => (props.hasClickAction ? themeGet('shadows.xl') : undefined)};
    color: inherit;
    cursor: ${(props) => (props.hasClickAction ? 'pointer' : 'unset')};
    outline: 0;
    text-decoration: none;

    img {
      ${(props) => props.foregroundMediaCss}
    }
  }
`

const RichList = styled(ContentfulRichText)`
  &&& {
    padding-inline-start: 24px;
    margin-left: 0;
  }

  &&& li > p {
    margin: ${(props) => themeGet(`space.${props.listMargin}`)} 0;
    padding: 0;
  }
`

const RoundedIframe = styled.iframe`
  border-radius: ${themeGet('borderRadii.xl')} ${themeGet('borderRadii.xl')}
    ${(props) =>
      props.hasClickAction ? '0 0' : `${themeGet('borderRadii.xl')} ${themeGet('borderRadii.xl')}`};
`

const MediaCard = ({
  asset,
  badgeText,
  callToAction,
  condense = 'lg',
  contentTypeId,
  coupon,
  foregroundMedia,
  heading,
  headingColor = 'text.dark',
  internalName,
  list,
  objectFit = 'cover',
  objectPosition = 'center',
  overline,
  lazyLoad = true,
  subheading,
  subheadingColor = 'text.base',
  youtubeVideoUrl,
  handleOpen,
  handleClick,
  hideClipboardOnMobile = true,
  ...props
}) => {
  const onCouponClick = useRef(null)
  const callToActionUrl = callToAction?.longUrl || callToAction?.url
  const hasClickAction = callToAction || coupon

  const assetSrc = asset && (typeof asset === 'string' ? asset : asset.file?.url)
  const foregroundMediaSrc =
    foregroundMedia && (typeof foregroundMedia === 'string' ? foregroundMedia : foregroundMedia.file?.url)
  const foregroundMediaCss = css`
    transform: scale(1.05);
    transition: transform ${themeGet('timingFunctions.easeInOut')} ${themeGet('duration.normal')};
  `

  const listProps = {
    color: subheadingColor,
    hasSubheading: Boolean(subheading),
    listMargin: CONDENSE_PROPS[condense].listMargin,
    textStyle: CONDENSE_PROPS[condense].bodyTextStyle,
  }
  const listNode =
    list && (list.data ? <RichList body={list} {...listProps} /> : <Text {...listProps}>{list}</Text>)

  const clickHandler = () => {
    if (coupon) {
      onCouponClick.current()
    } else if (callToAction?.contentTypeId == 'modal') {
      handleOpen(callToAction.modalContent, callToAction.analyticsTracking)
    } else if (callToActionUrl) {
      const gaParams = { contentTypeId, internalName }
      handleOpen && handleOpen(gaParams, callToAction.analyticsTracking)
      if (handleClick) {
        handleClick()
      } else {
        window.open(callToActionUrl, callToAction?.openInNewTab ? '_blank' : '_self')
      }
    }
  }

  return (
    <ContainerFlex
      bg='background.lightest'
      borderRadius='xl'
      borderWidth={0}
      flexDirection='column'
      foregroundMediaCss={hasClickAction ? foregroundMediaCss : ''}
      hasClickAction={hasClickAction}
      height='100%'
      justifyContent='space-between'
      onClick={clickHandler}
      {...(hasClickAction ? { boxShadowSize: 'md' } : {})}
      {...props}
    >
      <Box>
        {youtubeVideoUrl ? (
          <Box height={CONDENSE_PROPS[condense].backgroundMediaHeight}>
            <RoundedIframe
              allowFullScreen
              data-testid='youtubeThumbnail'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
              frameBorder='0'
              hasClickAction={hasClickAction}
              height={CONDENSE_PROPS[condense].backgroundMediaHeight}
              src={youtubeVideoUrl}
              title={heading}
              width='100%'
            />
          </Box>
        ) : (
          assetSrc && (
            <Relative>
              {badgeText && (
                <Absolute
                  top={CONDENSE_PROPS[condense].badgePosition}
                  left={CONDENSE_PROPS[condense].badgePosition}
                >
                  <Badge color='background.darkest'>{badgeText}</Badge>
                </Absolute>
              )}
              <BackgroundImage
                data-testid='backgroundImage'
                borderRadius='xl'
                height={CONDENSE_PROPS[condense].backgroundMediaHeight}
                image={assetSrc}
                loading={lazyLoad ? 'lazy' : 'eager'}
                objectFit={objectFit}
                objectPosition={objectPosition}
                {...(hasClickAction ? { rounded: 'top' } : {})}
              >
                {foregroundMediaSrc && (
                  <Flex alignItems='center' height='100%' justifyContent='center' width='100%'>
                    <Image height={CONDENSE_PROPS[condense].foregroundMediaHeight} src={foregroundMediaSrc} />
                  </Flex>
                )}
              </BackgroundImage>
            </Relative>
          )
        )}
        <Box
          mb={1}
          pb={hasClickAction ? CONDENSE_PROPS[condense].elementSpacing : 3}
          pt={CONDENSE_PROPS[condense].borderSpacing}
          px={CONDENSE_PROPS[condense].borderSpacing}
        >
          {overline && (
            <Text color='text.light' mb={1} textStyle='overline'>
              {overline}
            </Text>
          )}
          {heading && (
            <Text
              color={headingColor}
              mb={CONDENSE_PROPS[condense].elementSpacing}
              textStyle={CONDENSE_PROPS[condense].headingTextStyle}
            >
              {heading}
            </Text>
          )}
          {subheading && (
            <Text color={subheadingColor} textStyle={CONDENSE_PROPS[condense].bodyTextStyle}>
              {subheading}
            </Text>
          )}
          {listNode}
        </Box>
      </Box>
      {callToAction && !coupon && (
        <Box px={CONDENSE_PROPS[condense].borderSpacing} pb={CONDENSE_PROPS[condense].borderSpacing}>
          {callToAction.contentTypeId === 'modal' ? (
            <Button size='small' variation='fill'>
              {callToAction.buttonText}
            </Button>
          ) : (
            <Text textStyle='subheading6'>
              <ContentfulCallToAction
                {...callToAction}
                onClick={(event) => {
                  event.stopPropagation()
                  if (handleClick) {
                    event.preventDefault()
                    handleClick()
                  }
                  handleOpen &&
                    callToAction?.analyticsTracking &&
                    handleOpen(null, callToAction.analyticsTracking)
                }}
              />
            </Text>
          )}
        </Box>
      )}
      {coupon && (
        <Box px={CONDENSE_PROPS[condense].borderSpacing}>
          <CouponClipboard
            {...coupon}
            buttonText={null}
            mb={CONDENSE_PROPS[condense].borderSpacing}
            handleExternalClick={onCouponClick}
            hideIconOnMobile={hideClipboardOnMobile}
          />
        </Box>
      )}
    </ContainerFlex>
  )
}

export default MediaCard
